import { z } from "zod";
import { entityIdSchema, INT32_MAX } from "../common";

/**
 * Web publication precondition for CPP (both variants), induction and EP.
 * This is the singleton pointer when the form opened, not the latest pointer
 * fetched before submission. Explicit null means no generated version existed;
 * omission is an unsupported older web form and must never bypass the check.
 * Native CPP uses cppSourceCurrentVersionId for the actual shared singleton
 * pointer; cppCurrentVersion is only the optional FULL-answer prefill.
 */
export const WebDocumentGenerationSourceSchema = z.object({
  sourceCurrentVersionId: entityIdSchema.max(200).nullable(),
}).strict();
export type WebDocumentGenerationSource = z.infer<typeof WebDocumentGenerationSourceSchema>;

const versionFields = {
  documentId: entityIdSchema,
  versionId: entityIdSchema,
  versionNumber: z.number().int().min(1).max(INT32_MAX),
  createdAt: z.string().datetime(),
};

/**
 * Answers retained by the chosen generated version, separate from live Project
 * facts. Null and empty text are recorded answers. A field in `unrecorded` was
 * not stored by that version; its null placeholder must not imply an authored
 * clear. Readers must show that distinction before issuing a replacement.
 * For SI/EP native generation, echo this object's `versionId` as the draft's
 * top-level `basisVersionId`; a null `documentOnlyCurrentVersion` means an
 * explicit null basis. The stamp records which answers were opened, not a
 * fresh pointer read made when submitting. A CPP source stamp is separate:
 * its current singleton may be lightweight and therefore have no full-answer
 * prefill at all.
 * Wire objects permit additive fields for installed clients, as CPP prefill does.
 */
export const DocumentOnlyCurrentVersionPrefillV1Schema = z.discriminatedUnion("documentType", [
  z.object({
    ...versionFields,
    documentType: z.literal("SITE_INDUCTION"),
    answers: z.object({ reportingProcedures: z.string().nullable() }),
    unrecorded: z.array(z.string()),
  }),
  z.object({
    ...versionFields,
    documentType: z.literal("EMERGENCY_PLAN"),
    answers: z.object({ emergencyContacts: z.string().nullable() }),
    unrecorded: z.array(z.string()),
  }),
]);
export type DocumentOnlyCurrentVersionPrefillV1 = z.infer<typeof DocumentOnlyCurrentVersionPrefillV1Schema>;

/** Required response key lets a newer editor detect a server without prefill. */
export const DocumentOnlyRegenerationPrefillResponseV1Schema = z.object({
  documentOnlyCurrentVersion: DocumentOnlyCurrentVersionPrefillV1Schema.nullable(),
});
export type DocumentOnlyRegenerationPrefillResponseV1 = z.infer<typeof DocumentOnlyRegenerationPrefillResponseV1Schema>;
